/**
 * @file useHomeBannerTouch.js
 * @description 首页 Banner 滑动手势：左右切屏，抑制误触点击
 * @module features/home/composables/useHomeBannerTouch
 */

import { onUnmounted, ref, watch } from 'vue'

/** 触发切屏的最小水平位移（px） */
const SWIPE_THRESHOLD_PX = 40

/** 垂直偏移超过该值视为页面滚动，不触发切屏 */
const SWIPE_MAX_VERTICAL_PX = 72

/** 滑动后抑制封面点击的时长（ms） */
const SUPPRESS_CLICK_MS = 350

/** 分页点区域：触摸起始于此时不参与滑动手势 */
const DOT_INTERACTION_SELECTOR =
  '.home-banner-frame__dot, .home-banner-frame__dots, .home-banner-h5__dot, .home-banner-h5__dots, .home-banner-web__dot, .home-banner-web__dots'

/**
 * @param {EventTarget|null} target
 */
function isDotInteractionTarget(target) {
  return target instanceof Element && Boolean(target.closest(DOT_INTERACTION_SELECTOR))
}

/**
 * @param {import('vue').Ref<HTMLElement|null>} targetRef
 * @param {{ onSwipePrev: () => void, onSwipeNext: () => void }} handlers
 */
export function useHomeBannerTouch(targetRef, handlers) {
  const suppressClickUntil = ref(0)
  let startX = 0
  let startY = 0
  let tracking = false

  function resetTracking() {
    tracking = false
  }

  /**
   * @param {TouchEvent} event
   */
  function onTouchStart(event) {
    if (event.touches.length !== 1) return
    if (isDotInteractionTarget(event.target)) {
      resetTracking()
      return
    }
    startX = event.touches[0].clientX
    startY = event.touches[0].clientY
    tracking = true
  }

  /**
   * @param {TouchEvent} event
   */
  function onTouchEnd(event) {
    if (!tracking) return
    resetTracking()
    if (isDotInteractionTarget(event.target)) return
    if (event.changedTouches.length !== 1) return

    const deltaX = event.changedTouches[0].clientX - startX
    const deltaY = event.changedTouches[0].clientY - startY

    if (Math.abs(deltaY) > SWIPE_MAX_VERTICAL_PX) return
    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return

    suppressClickUntil.value = Date.now() + SUPPRESS_CLICK_MS

    if (deltaX < 0) {
      handlers.onSwipeNext()
    } else {
      handlers.onSwipePrev()
    }
  }

  function bindTouch() {
    const el = targetRef.value
    if (!el) return
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', resetTracking, { passive: true })
  }

  function unbindTouch() {
    const el = targetRef.value
    if (!el) return
    el.removeEventListener('touchstart', onTouchStart)
    el.removeEventListener('touchend', onTouchEnd)
    el.removeEventListener('touchcancel', resetTracking)
  }

  /** 滑动切屏后的短时间内忽略封面点击 */
  function shouldSuppressComicClick() {
    return Date.now() < suppressClickUntil.value
  }

  watch(targetRef, (el, prev) => {
    if (prev) unbindTouch()
    if (el) bindTouch()
  })

  onUnmounted(() => {
    unbindTouch()
  })

  return {
    shouldSuppressComicClick,
  }
}
